<template>
    <DialogLayout class-name="candidate-summary-dialog" @close="handleClose">
        <template #title>
            <div class="dialog-title-content">
                <img src="@/assets/icons/cup.svg" alt="note" class="header-icon" />
                <span>{{ t('candidate.aiScore') }}</span>
            </div>
        </template>

        <div class="summary-content">
            <!-- 候选人基本信息 - 完全复制 EmployeeDetailHeader 样式 -->
            <div class="employee-detail-header">
                <div class="header-avatar">
                    <img v-if="data.avatarUrl" :src="data.avatarUrl" alt="avatar" class="avatar-img" />
                    <div v-else class="avatar-placeholder">
                        <img src="@/assets/images/default_user.png" alt="user" class="user-icon" />
                    </div>
                </div>
                <div class="header-info">
                    <div class="name-row">
                        <div class="name-section">
                            <div v-hover-tip.over="data.name" class="name">{{ data.name }}</div>
                            <div v-if="data.nameKana" v-hover-tip.over="data.nameKana" class="nickname">（{{
                                data.nameKana }}）</div>
                        </div>
                        <div v-if="data.specialSkillTag" class="tags-section">
                            <el-tag v-for="(tag, index) in data.specialSkillTag" :key="index" class="skill-tag"
                                type="warning" effect="plain">
                                {{ tag }}
                            </el-tag>
                        </div>
                    </div>
                    <div class="basic-info-row">
                        <span v-if="data.nationality">{{ data.nationality }}</span>
                        <span v-if="data.age" class="separator">|</span>
                        <span v-if="data.age">{{ data.age }}{{ t('employee.detail.ageUnit') }}</span>
                        <span v-if="data.gender" class="separator">|</span>
                        <span v-if="data.gender">{{ data.gender }}</span>
                        <span v-if="data.highestEducationLevel" class="separator">|</span>
                        <span v-if="data.highestEducationLevel">{{ data.highestEducationLevel }}</span>
                    </div>
                    <div class="contact-row">
                        <div v-if="data.phoneCountryCode || data.phone" class="contact-item">
                            <img src="@/assets/icons/phone.svg" alt="phone" class="contact-icon" />
                            <span>{{ data.phoneCountryCode }}{{ data.phone }}</span>
                        </div>
                        <div v-if="data.email" class="contact-item">
                            <img src="@/assets/icons/email.svg" alt="email" class="contact-icon" />
                            <span>{{ data.email }}</span>
                        </div>
                    </div>
                    <div v-if="data.residenceCardAddress" class="work-row">
                        <img src="@/assets/icons/location.svg" alt="location" class="work-icon" />
                        <div v-hover-tip.over="data.residenceCardAddress" class="work-address">{{
                            data.residenceCardAddress || '-' }}</div>
                        <span v-if="data.japaneseLevel" class="separator">|</span>
                        <span v-if="data.japaneseLevel">{{ data.japaneseLevel }}</span>
                    </div>
                </div>
                <div class="header-action ai-score-section">
                    <div class="ai-score-display">
                        <div class="ai-score-number">{{ data.score }}</div>
                        <div class="ai-score-items">
                            <div class="ai-score-item">
                                <img src="@/assets/icons/cup.svg" alt="trophy" class="item-icon" />
                                <span class="item-text">{{ t('candidate.aiScore') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="summary-divider"></div>
            <!-- 摘要文本 -->
            <p class="summary-text">
                {{ data.content }}
            </p>
        </div>
    </DialogLayout>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue'
import type { AiScoreDialogProps } from './ai-score-dialog-types'

const { t } = useI18n()

defineProps<AiScoreDialogProps>()

const emit = defineEmits<{
    close: []
}>()

const handleClose = () => {
    emit('close')
}
</script>

<style lang="scss" scoped>
.candidate-summary-dialog {
    width: 760px;
    background: linear-gradient(179deg, #EFF0FF 1%, #FFFFFF 30%);
    box-sizing: border-box;
    border: 1px solid #DCDDFD;
    border-radius: 12px;

    :deep(.dialog-header) {
        padding: 20px 24px;
        background: transparent;
    }

    :deep(.dialog-content) {
        padding: 0px;
        // max-height: 600px;
        overflow-y: auto;
        background: transparent;
    }

    :deep(.dialog-footer) {
        display: none;
    }

    .dialog-title-content {
        display: flex;
        align-items: center;
        gap: 8px;

        .header-icon {
            width: 20px;
            height: 20px;
        }
    }

    .summary-content {
        display: flex;
        flex-direction: column;
        gap: 10px;
        background: transparent;
        max-height: 700px;
        padding-bottom: 24px;
        overflow: hidden;
        

        // 完全复制 EmployeeDetailHeader 的样式
        .employee-detail-header {
            display: flex;
            gap: 8px;
            padding: 10px 16px;
            background: transparent;
            border-radius: 8px;
            align-items: center;
            margin: 0 20px;
        }

        .header-avatar {
            flex-shrink: 0;
            width: 90px;
            height: 90px;

            .avatar-img {
                width: 100%;
                height: 100%;
                border-radius: 50%;
                object-fit: cover;
            }

            .avatar-placeholder {
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background: #E9ECEF;
                display: flex;
                align-items: center;
                justify-content: center;

                .user-icon {
                    width: 90px;
                    height: 90px;
                }
            }
        }

        .header-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .name-row {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 16px;

            .name-section {
                display: flex;
                align-items: center;
                gap: 8px;

                .name {
                        font-size: 18px;
                        font-weight: bold;
                        color: #212429;
                        max-width: 200px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .nickname {
                        max-width: 200px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        font-size: 14px;
                        font-weight: normal;
                        color: #1D2129;
                    }
            }

            .tags-section {
                display: flex;
                gap: 12px;
                flex-shrink: 0;

                .skill-tag {
                    background: #FAECD8 !important;
                    color: #B88230 !important;
                    border: none !important;
                    border-radius: 20px !important;
                    padding: 4px 8px !important;
                    font-size: 12px !important;
                    font-weight: 500 !important;
                    height: auto !important;
                    line-height: 17px !important;
                }
            }
        }

        .basic-info-row {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 500;
            line-height: 17px;
            color: #495057;

            .separator {
                color: #C9D3D9;
                margin: 0 4px;
            }
        }

        .contact-row {
            display: flex;
            align-items: center;
            gap: 24px;
            font-size: 12px;
            font-weight: 500;
            color: #495057;
            line-height: 17px;

            .contact-item {
                display: flex;
                align-items: center;
                gap: 8px;

                .contact-icon {
                    width: 16px;
                    height: 16px;
                    opacity: 0.6;
                }
            }
        }

        .work-row {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 500;
            color: #495057;
            line-height: 17px;

            .work-icon {
                width: 16px;
                height: 16px;
                opacity: 0.6;
            }

            .separator {
                color: #C9D3D9;
                margin: 0 4px;
            }
        }
                .work-address {
                    max-width: 300px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

        .summary-divider {
            width: 100%;
            height: 1px;
            background-color: #EAECF0;
            margin: 0 24px;
        }

        .summary-text {

            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
            color: #212429;
            text-align: justify;
            padding: 0 24px;
            margin: 0;
            white-space: pre-wrap;
            flex: 1;
            overflow: auto;
        }
    }

    .header-action {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        margin-left: auto;

        .summary-btn {
            background: #F8F9FF;
            border: 1px solid #8755DE;
            color: #8755DE;
            border-radius: 8px;
            padding: 8px 16px;
            font-size: 14px;
            font-weight: 500;
            height: auto;
            line-height: 20px;


            &:active {
                background: #E8EBFF;
            }
        }

        &.ai-score-section {
            align-items: flex-start;
        }

        .ai-score-display {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;
            max-width: 180px;

            .ai-score-number {
                font-size: 36px;
                font-weight: 500;
                color: #7A5CF2;
                line-height: 1;
                margin-right: 12px;
            }

            .ai-score-items {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                gap: 4px;
                width: 100%;

                .ai-score-item {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 6px 0;
                    transition: opacity 0.2s;



                    .item-icon {
                        width: 14px;
                        height: 14px;
                        flex-shrink: 0;
                        filter: brightness(0) saturate(100%) invert(37%) sepia(73%) saturate(2234%) hue-rotate(237deg) brightness(97%) contrast(93%);
                    }

                    .item-text {
                        font-size: 14px;
                        font-weight: normal;
                        color: #7A5CF2;
                    }

                    .item-arrow {
                        width: 16px;
                        height: 16px;
                        flex-shrink: 0;
                        filter: brightness(0) saturate(100%) invert(32%) sepia(65%) saturate(2456%) hue-rotate(249deg) brightness(91%) contrast(90%);
                    }
                }
            }
        }
    }
}
</style>
